import React, { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = 'Navn skal udfyldes';

    if (!formData.telefon.trim()) {
      errs.telefon = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9+\s-]{8,15}$/.test(formData.telefon.trim())) {
      errs.telefon = 'Indtast et gyldigt telefonnummer (min. 8 cifre)';
    }

    if (!formData.email.trim()) {
      errs.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast en gyldig e-mailadresse';
    }

    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = 'Beskriv venligst kort hvad du ønsker hjælp til';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setStatusMessage(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefonnummer: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url // Mandatory honeypot
        })
      });

      if (response.ok) {
        setStatusMessage({
          type: 'success',
          text: 'Tak for din besked. Jeg vender tilbage til dig hurtigst muligt.'
        });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Der opstod en fejl ved afsendelse. Ring gerne direkte på 24 91 43 13.'
        });
      }
    } catch (err) {
      console.error('Webform error:', err);
      setStatusMessage({
        type: 'error',
        text: 'Der kunne ikke oprettes forbindelse. Kontrollér din internetforbindelse eller ring direkte på 24 91 43 13.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 sm:p-10 rounded-3xl border border-[#E8DFD5] shadow-xs">
      {/* Honeypot Input (Hidden from humans) */}
      <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
        <label htmlFor="website_url">Website URL</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex={-1}
          value={formData.website_url}
          onChange={handleChange}
          autoComplete="off"
        />
      </div>

      {statusMessage && (
        <div
          className={`p-4 rounded-2xl text-sm font-medium ${
            statusMessage.type === 'success'
              ? 'bg-[#E8DFD5] text-[#4A423D] border border-[#D8CDBF]'
              : 'bg-[#F5EBE6] text-[#935445] border border-[#E7CCC4]'
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Navn */}
      <div>
        <label htmlFor="navn" className="block text-sm font-medium text-[#2E2A27] mb-2">
          Fulde navn <span className="text-[#C78D7D]">*</span>
        </label>
        <input
          type="text"
          id="navn"
          name="navn"
          value={formData.navn}
          onChange={handleChange}
          placeholder="f.eks. Mette Hansen"
          className={`w-full px-4 py-3 rounded-xl border bg-[#FAF7F2] text-[#2E2A27] focus:outline-hidden focus:ring-2 focus:ring-[#98866C] transition-all ${
            errors.navn ? 'border-[#C78D7D] bg-[#FAF4F2]' : 'border-[#D8CDBF]'
          }`}
        />
        {errors.navn && <p className="text-xs text-[#C78D7D] mt-1">{errors.navn}</p>}
      </div>

      {/* Telefon & Email grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="telefon" className="block text-sm font-medium text-[#2E2A27] mb-2">
            Telefonnummer <span className="text-[#C78D7D]">*</span>
          </label>
          <input
            type="tel"
            id="telefon"
            name="telefon"
            value={formData.telefon}
            onChange={handleChange}
            placeholder="f.eks. 24 91 43 13"
            className={`w-full px-4 py-3 rounded-xl border bg-[#FAF7F2] text-[#2E2A27] focus:outline-hidden focus:ring-2 focus:ring-[#98866C] transition-all ${
              errors.telefon ? 'border-[#C78D7D] bg-[#FAF4F2]' : 'border-[#D8CDBF]'
            }`}
          />
          {errors.telefon && <p className="text-xs text-[#C78D7D] mt-1">{errors.telefon}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#2E2A27] mb-2">
            E-mailadresse <span className="text-[#C78D7D]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="f.eks. mette@eksempel.dk"
            className={`w-full px-4 py-3 rounded-xl border bg-[#FAF7F2] text-[#2E2A27] focus:outline-hidden focus:ring-2 focus:ring-[#98866C] transition-all ${
              errors.email ? 'border-[#C78D7D] bg-[#FAF4F2]' : 'border-[#D8CDBF]'
            }`}
          />
          {errors.email && <p className="text-xs text-[#C78D7D] mt-1">{errors.email}</p>}
        </div>
      </div>

      {/* Beskrivelse */}
      <div>
        <label htmlFor="beskrivelse" className="block text-sm font-medium text-[#2E2A27] mb-2">
          Hvad ønsker du hjælp til? <span className="text-[#C78D7D]">*</span>
        </label>
        <textarea
          id="beskrivelse"
          name="beskrivelse"
          rows={4}
          value={formData.beskrivelse}
          onChange={handleChange}
          placeholder="Fortæl kort om dine tanker, ønsker eller udfordringer..."
          className={`w-full px-4 py-3 rounded-xl border bg-[#FAF7F2] text-[#2E2A27] focus:outline-hidden focus:ring-2 focus:ring-[#98866C] transition-all ${
            errors.beskrivelse ? 'border-[#C78D7D] bg-[#FAF4F2]' : 'border-[#D8CDBF]'
          }`}
        />
        {errors.beskrivelse && <p className="text-xs text-[#C78D7D] mt-1">{errors.beskrivelse}</p>}
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-4 px-6 rounded-full bg-[#98866C] text-white font-semibold text-base hover:bg-[#83735C] transition-all duration-200 cursor-pointer shadow-xs disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
            <span>Sender besked...</span>
          </>
        ) : (
          <span>Send besked og book samtale</span>
        )}
      </button>
    </form>
  );
}
